import React, { useMemo } from 'react';

import { SortableTable } from '~/components/core';
import EmptyState from '~/components/core/EmptyState';
import ThreeDotsMenu from '~/components/core/ThreeDotsMenu';
import { useLobConfiguration } from '~/components/hooks/useLobConfiguration';
import Switch from '~/components/ui/atoms/Switch';
import type { GuardrailRuleInfo } from '~/server_shared/openapi/data-contracts';
import { subOrgIdToNameDict } from '~/Utils';
import { getLobDescription } from '~/Utils/lobUtils';

import type { SysconfigOrganization } from '../types';

const OUTCOME_LABELS: Record<GuardrailRuleInfo['outcome'], string> = {
  warn: 'Warning',
  block: 'Block',
  route_to_human: 'Route to Human',
};

interface GuardrailRulesTableProps {
  organization: SysconfigOrganization;
  rules: GuardrailRuleInfo[];
  isProcessing: boolean;
  emptyStateText: string;
  onEditRule: (rule: GuardrailRuleInfo) => void;
  onDeleteRule: (rule: GuardrailRuleInfo) => void | Promise<void>;
  onEnabledChanged: (rule: GuardrailRuleInfo) => void | Promise<void>;
}

interface Column {
  id: string;
  label: string;
  numeric?: boolean;
  disableSort?: boolean;
  width?: string;
  specialCell?: (rule: GuardrailRuleInfo) => React.ReactNode;
}

export const GuardrailRulesTable: React.FC<GuardrailRulesTableProps> = ({
  organization,
  rules,
  isProcessing,
  emptyStateText,
  onEditRule,
  onDeleteRule,
  onEnabledChanged,
}) => {
  const { lobConfigurationsDict } = useLobConfiguration();
  // Identical for every row — build once per `organization`, not per cell render.
  const subOrgIdToName = useMemo(() => subOrgIdToNameDict(organization), [organization]);

  const actionOptions = (rule: GuardrailRuleInfo) => [
    {
      onClick: () => onEditRule(rule),
      node: 'Edit',
      key: 'editRule',
    },
    {
      onClick: () => onDeleteRule(rule),
      withConfirmProps: {
        title: 'Delete Guardrail?',
        contentText: `Are you sure you want to delete "${rule.name}"?`,
        primaryButtonName: 'Delete',
      },
      node: 'Delete',
      key: 'deleteRule',
    },
  ];

  const columns: Column[] = [
    { id: 'name', label: 'Name' },
    {
      id: 'lob',
      label: 'LOB',
      specialCell: (rule) =>
        rule.scope?.is_all_lobs
          ? 'All'
          : (rule.scope?.lobs ?? []).map((lobKey) => getLobDescription(lobKey, lobConfigurationsDict)).join(', '),
    },
    {
      id: 'outcome',
      label: 'Type',
      specialCell: (rule) => OUTCOME_LABELS[rule.outcome],
    },
    {
      id: 'enable',
      numeric: false,
      label: 'Enable',
      disableSort: true,
      specialCell: (rule) => (
        <Switch onChange={() => onEnabledChanged(rule)} disabled={isProcessing} checked={!!rule.is_enabled} />
      ),
    },
    {
      id: 'actions',
      label: 'Actions',
      width: '90px',
      specialCell: (rule) => (
        <div>
          <ThreeDotsMenu options={actionOptions(rule)} />
        </div>
      ),
    },
  ];

  if (organization.sub_organizations_enabled) {
    columns.splice(1, 0, {
      id: 'sub_organization_ids',
      label: 'Sub Organizations',
      specialCell: (rule) => {
        const res = rule.scope?.is_all_sub_orgs
          ? Object.values(subOrgIdToName)
          : (rule.scope?.sub_organization_ids ?? []).map((id) => subOrgIdToName[id]).filter(Boolean);
        return res.join(', ');
      },
    });
  }

  return (
    <SortableTable
      columns={columns}
      rows={rules}
      disableSortByUser
      keepRowsOrder
      emptyStateComponent={<EmptyState subtitle={emptyStateText} />}
    />
  );
};

export default GuardrailRulesTable;
